
import { supabase } from '@/integrations/supabase/client';
import { CreditCardBill } from '@/types/bill';
import { encryptField, decryptField } from './encryption-service';

export const saveBillToSupabase = async (bill: CreditCardBill): Promise<void> => {
  console.log('[Supabase Bills] Saving bill:', bill.id);
  
  try {
    // Encrypt sensitive fields
    const totalAmountEncrypted = await encryptField(String(bill.totalAmount));
    const paidAmountEncrypted = await encryptField(String(bill.paidAmount));
    
    const billData = {
      id: bill.id,
      card_id: bill.cardId,
      period_start: bill.periodStart,
      period_end: bill.periodEnd,
      due_date: bill.dueDate,
      total_amount: 0,
      total_amount_encrypted: totalAmountEncrypted.encrypted,
      total_amount_nonce: totalAmountEncrypted.nonce,
      paid_amount: 0,
      paid_amount_encrypted: paidAmountEncrypted.encrypted,
      paid_amount_nonce: paidAmountEncrypted.nonce,
      status: bill.status,
      transaction_ids: bill.transactionIds,
      payment_ids: bill.paymentIds,
      user_id: (await supabase.auth.getUser()).data.user?.id
    };

    const { error } = await supabase
      .from('credit_card_bills')
      .upsert(billData);

    if (error) {
      console.error('[Supabase Bills] Error saving bill:', error);
      throw new Error(`Erro ao salvar fatura: ${error.message}`);
    }

    console.log('[Supabase Bills] Bill saved successfully:', bill.id);
  } catch (error) {
    console.error('[Supabase Bills] Encryption error:', error);
    throw new Error('Erro ao criptografar dados da fatura');
  }
};

export const loadBillsFromSupabase = async (): Promise<CreditCardBill[]> => {
  console.log('[Supabase Bills] Loading bills from Supabase...');

  const { data, error } = await supabase
    .from('credit_card_bills')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Supabase Bills] Error loading bills:', error);
    throw new Error(`Erro ao carregar faturas: ${error.message}`);
  }

  const bills: CreditCardBill[] = await Promise.all((data || []).map(async (row) => {
    // Decrypt sensitive fields
    const totalAmount = await decryptField({
      encrypted: row.total_amount_encrypted,
      nonce: row.total_amount_nonce
    });
    
    const paidAmount = await decryptField({
      encrypted: row.paid_amount_encrypted,
      nonce: row.paid_amount_nonce
    });
    
    return {
      id: row.id,
      cardId: row.card_id,
      periodStart: row.period_start,
      periodEnd: row.period_end,
      dueDate: row.due_date,
      totalAmount: totalAmount ? Number(totalAmount) : Number(row.total_amount || 0),
      paidAmount: paidAmount ? Number(paidAmount) : Number(row.paid_amount || 0),
      status: row.status as 'pendente' | 'parcialmente_paga' | 'paga',
      transactionIds: (Array.isArray(row.transaction_ids) ? row.transaction_ids : []) as string[],
      paymentIds: (Array.isArray(row.payment_ids) ? row.payment_ids : []) as string[],
      createdAt: row.created_at
    };
  }));

  console.log(`[Supabase Bills] Loaded ${bills.length} bills`);
  return bills;
};

export const deleteBillFromSupabase = async (billId: string): Promise<void> => {
  console.log('[Supabase Bills] Deleting bill:', billId);

  const { error } = await supabase
    .from('credit_card_bills')
    .delete()
    .eq('id', billId);

  if (error) {
    console.error('[Supabase Bills] Error deleting bill:', error);
    throw new Error(`Erro ao excluir fatura: ${error.message}`);
  }

  console.log('[Supabase Bills] Bill deleted successfully:', billId);
};

export const isUserAuthenticated = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();
  return !!user;
};
